import React, { useState } from "react";
import { navigate } from "gatsby";
import Icon from "@icon-park/react/es/all";
import { Text, theme } from "@ifgengineering/component-library";
import { Commitment } from "@ifgengineering/client-invest-sdk";
import {
  EisFundPageContainer,
  EisFundHeaderWrapper,
  EisFundHeader,
  EisFundHeaderContent,
  EisFundHeaderTextContainer,
  BackButton,
  EisFundContentWrapper,
  EisFundContent,
} from "./styled";
import { EisFundStartup } from "./types";
import EisFundQuickActions from "./EisFundQuickActions";
import EisFundSearch from "./EisFundSearch";
import EisFundAccountTabs from "./EisFundAccountTabs";
import EisFundStartupsList from "./EisFundStartupsList";
import EisFundFilterModal, { EisFundFilterValues } from "./EisFundFilterModal";
import GlossaryModal from "@components/Glossary/Glossary.component";

interface EisFundPageProps {
  startups: EisFundStartup[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  total?: number;
  onLoadMore?: () => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  isSearching?: boolean;
  filters?: EisFundFilterValues;
  onFiltersApply?: (filters: EisFundFilterValues) => void;
  onFiltersReset?: () => void;
  onBack?: () => void;
  onEisDocumentsClick?: () => void;
  commitments?: Commitment[];
  selectedCommitmentId?: string;
  onCommitmentChange?: (commitmentId: string) => void;
  onStartupClick?: (startup: EisFundStartup) => void;
}

const DEFAULT_FILTERS: EisFundFilterValues = {
  taxBenefit: "all",
  investmentStatuses: [],
};

const EisFundPage: React.FC<EisFundPageProps> = ({
  startups,
  loading = false,
  loadingMore = false,
  hasMore = false,
  total = 0,
  onLoadMore,
  searchQuery: externalSearchQuery,
  onSearchChange: externalOnSearchChange,
  isSearching = false,
  filters: externalFilters,
  onFiltersApply,
  onFiltersReset,
  onBack,
  onEisDocumentsClick,
  commitments = [],
  selectedCommitmentId,
  onCommitmentChange,
  onStartupClick,
}) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const [internalFilters, setInternalFilters] =
    useState<EisFundFilterValues>(DEFAULT_FILTERS);

  const searchQuery = externalSearchQuery ?? internalSearchQuery;
  const handleSearchChange = externalOnSearchChange ?? setInternalSearchQuery;
  const filters = externalFilters ?? internalFilters;
  const handleFiltersApply = onFiltersApply ?? setInternalFilters;
  const handleFiltersReset =
    onFiltersReset ?? (() => setInternalFilters(DEFAULT_FILTERS));

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showGlossaryModal, setShowGlossaryModal] = useState(false);

  const isFilterActive =
    filters.taxBenefit !== "all" || filters.investmentStatuses.length > 0;

  const handleBack = () => (onBack ? onBack() : navigate(-1));

  return (
    <EisFundPageContainer data-testid="eis-fund-page">
      <EisFundHeaderWrapper>
        <EisFundHeader>
          <EisFundHeaderContent>
            <BackButton onClick={handleBack} data-testid="eis-back-button">
              <Icon
                type="LeftSmall"
                theme="filled"
                fill={theme.colors.WHITE}
                size={24}
              />
            </BackButton>
            <EisFundHeaderTextContainer>
              <Text type="H24" color="WHITE" fontFamily="archiasemibold">
                Cur8 EIS Fund
              </Text>
              <Text type="P16" color="SLATE400">
                These are the startups you've invested in through the EIS Fund.
                {total > 0 && ` (${total} total)`}
              </Text>
            </EisFundHeaderTextContainer>
          </EisFundHeaderContent>
        </EisFundHeader>
      </EisFundHeaderWrapper>

      <EisFundContentWrapper>
        <EisFundContent>
          <EisFundQuickActions
            onFilterClick={() => setShowFilterModal(true)}
            onGlossaryClick={() => setShowGlossaryModal(true)}
            onEisDocumentsClick={onEisDocumentsClick}
            isFilterActive={isFilterActive}
          />

          <EisFundSearch
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search"
            isLoading={isSearching}
          />

          <EisFundAccountTabs
            commitments={commitments}
            selectedCommitmentId={selectedCommitmentId}
            onCommitmentChange={onCommitmentChange}
          />

          <EisFundStartupsList
            startups={startups}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            searchQuery={searchQuery}
            isSearching={isSearching}
            onStartupClick={onStartupClick}
          />
        </EisFundContent>
      </EisFundContentWrapper>

      <EisFundFilterModal
        show={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        filters={filters}
        onApply={handleFiltersApply}
        onReset={handleFiltersReset}
      />

      <GlossaryModal
        show={showGlossaryModal}
        onClose={() => setShowGlossaryModal(false)}
        glossaryType="EIS_STATUS"
      />
    </EisFundPageContainer>
  );
};

export default EisFundPage;
